import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, CheckCircle, XCircle, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // "form", "success", "error"
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("No reset token provided. Please request a new password reset link.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setStatus("success");
      toast.success("Password reset successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password. The link may have expired.";
      setStatus("error");
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split">
      {/* Left side - Branding */}
      <div className="login-image">
        <img
          src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200"
          alt="Golf links"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 p-12">
          <img
            src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
            alt="Royal Dornoch Golf Club"
            className="w-40 h-40 object-contain mb-8 bg-white rounded-full p-2"
          />
          <h1 className="text-4xl text-white text-center mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
            Staff Training Portal
          </h1>
          <p className="text-white/80 text-center text-lg max-w-md">
            Delivering excellence through knowledge and service
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="h-px w-12 bg-white/40"></div>
            <span className="text-white/60 text-sm tracking-widest uppercase">Est. 1616</span>
            <div className="h-px w-12 bg-white/40"></div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 lg:p-12 bg-[var(--rd-cream)]">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
              alt="Royal Dornoch Golf Club"
              className="w-24 h-24 object-contain mb-4"
            />
            <h1 className="text-xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
              Staff Training
            </h1>
          </div>

          {/* Back to login link */}
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-[var(--rd-green)] hover:underline mb-8"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>

          {status === "form" && (
            <>
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-2xl text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  Reset Password
                </h2>
                <p className="text-[hsl(var(--muted-foreground))]">
                  Enter your new password below.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                      className="h-11 pl-10 pr-12 bg-white border-[hsl(var(--border))]"
                    />
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="reset-confirm-password-input"
                      className="h-11 pl-10 pr-12 bg-white border-[hsl(var(--border))]"
                    />
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-sm text-red-500">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white font-medium"
                  disabled={loading || password !== confirmPassword}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}

          {status === "success" && (
            <div className="text-center">
              <div className="w-16 h-16 bg-[var(--rd-green)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[var(--rd-green)]" />
              </div>
              <h2 className="text-2xl text-[var(--rd-green)] mb-3" style={{ fontFamily: "'Cinzel', serif" }}>
                Password Reset Complete
              </h2>
              <p className="text-[hsl(var(--muted-foreground))] mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Button
                className="w-full h-11 bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white font-medium"
                onClick={() => navigate("/login")}
                data-testid="go-to-login-btn"
              >
                Sign In Now
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-2xl text-[var(--rd-green)] mb-3" style={{ fontFamily: "'Cinzel', serif" }}>
                Reset Link Invalid
              </h2>
              <p className="text-[hsl(var(--muted-foreground))] mb-6">
                {errorMessage}
              </p>
              <Button
                className="w-full h-11 bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white font-medium"
                onClick={() => navigate("/forgot-password")}
                data-testid="request-new-link-btn"
              >
                Request New Reset Link
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
